import type { HelpArticle } from "../../types";
import { STRUCTURAL_HELP_FIXTURES } from "../../../calculations/structuralHelpFixtures";

const wall = STRUCTURAL_HELP_FIXTURES.masonry_bearing_wall_tms;
const shear = STRUCTURAL_HELP_FIXTURES.masonry_shear_wall_tms;
const lintel = STRUCTURAL_HELP_FIXTURES.masonry_lintel_tms;
const lintelReaction = lintel.reactions?.[0]?.byType ?? {};
const check = (fixture: typeof wall, key: string) => fixture.checks.find((entry) => entry.limitState === key)!;
const pct = (value: number | null): string => `${((value ?? 0) * 100).toFixed(1)}%`;
const n3 = (value: number | null): string => (value ?? 0).toLocaleString(undefined, { maximumFractionDigits: 3 });

export const masonrySheetsArticle: HelpArticle = {
  id: "masonry-sheets",
  title: "Masonry walls & lintels",
  description:
    "Use the three manual masonry sheets as exact published-row checks: out-of-plane wall, shear wall, and lintel.",
  tags: ["masonry", "CMU", "TMS", "MSJC", "wall", "shear wall", "lintel", "published row"],
  lastUpdated: "July 20, 2026",
  published: false,
  blocks: [
    {
      kind: "lead",
      text: "Firmark's three masonry sheets are deliberately tied to published configurations. Masonry Out-of-Plane Wall checks a zero-axial CMHA wall row. Masonry Shear Wall checks an exact reinforced-CMU row under strength-level P, M, and V. Masonry Lintel checks one exact CMHA lintel row under a limited service-load model. A selected row is a boundary: the runtime refuses mismatched conditions instead of interpolating.",
    },

    { kind: "heading", level: 2, text: "Start from the masonry family", id: "start" },
    { kind: "where", path: ["Calculations", "Project", "New calculation", "Structural calculators", "Masonry"] },
    {
      kind: "paragraph",
      text: "Choose the wall action you need rather than treating the three cards as interchangeable. Firmark assigns `MW`, `MSW`, or `ML`, seats the registry defaults, and opens the schema workspace. The first input card owns the database bindings. Browse selects the material or unit-strength record first, then the dependent published capacity row. The stable keys remain in the Calculation database block and resolve on every recompute.",
    },
    {
      kind: "shot",
      shotId: "structural-catalog-masonry",
      caption: "The Masonry family: out-of-plane wall, in-plane shear wall, and lintel are separate demand models.",
    },
    {
      kind: "table",
      caption: "The methods are fixed by the selected calculator and source row.",
      headers: ["Sheet", "Method shown on record", "Manual demand"],
      rows: [
        ["Masonry Out-of-Plane Wall", "2011 MSJC ASD / 2012 IBC", "Height, service lateral pressure, zero service axial"],
        ["Masonry Shear Wall", "TMS 402-16 / TMS 602-16 Strength", "Factored axial, overturning moment, in-plane shear"],
        ["Masonry Lintel", "2011 MSJC ASD / 2012 IBC", "Uniform, symmetric triangular, midpoint service loads"],
      ],
    },

    { kind: "heading", level: 2, text: "Exact rows and matching conditions", id: "exact-rows" },
    {
      kind: "paragraph",
      text: "A `reference_ready` selection supplies material properties. A `check_ready` capacity row also supplies the capacity used by the result. The sheet keeps the display label beside the exact stable key for audit. The runtime compares only the applicability fields encoded in each row. Lintel span and shear-wall length and height must match their rows; out-of-plane wall height and pressure generate demand against the selected fixed capacity row.",
    },
    { kind: "registry-group", calculatorId: "masonry_shear_wall_tms", groupId: "selection" },
    {
      kind: "callout",
      variant: "note",
      title: "No interpolation is runtime behavior",
      body: "The help text above states what the shipped resolver and adapters do: an unmatched row is refused. It is not advice for choosing a masonry system, reinforcement pattern, or alternate design method.",
    },

    { kind: "heading", level: 2, text: "Out-of-plane wall", id: "out-of-plane" },
    {
      kind: "paragraph",
      text: `The Out-of-Plane Wall sheet is narrower than its internal calculator id, masonry_bearing_wall_tms, might suggest. Its selected CMHA material and capacity row fix the 8 in CMU with #4 at 24 in centered, and the row assumes no applied axial load. The manual Axial load field is visible and requires zero; any nonzero value is refused. For ${wall.mark}, 12 ft height and 20 psf service lateral pressure are demand inputs used to generate moment and shear against that fixed capacity, not coordinates used to select the row.`,
    },
    {
      kind: "shot",
      shotId: "masonry-wall-inputs",
      caption: `${wall.mark}: fixed material and capacity row with 12 ft and 20 psf demand inputs and the required zero axial input.`,
    },
    {
      kind: "values",
      caption: `${wall.mark} · published-row comparisons per foot of wall.`,
      rows: [
        { label: "Out-of-plane flexure", value: `${n3(check(wall, "out_of_plane_flexure").demand)} / ${n3(check(wall, "out_of_plane_flexure").capacity)} lb-in/ft · ${pct(check(wall, "out_of_plane_flexure").dcr)}` },
        { label: "Out-of-plane shear", value: `${n3(check(wall, "out_of_plane_shear").demand)} / ${n3(check(wall, "out_of_plane_shear").capacity)} lb/ft · ${pct(check(wall, "out_of_plane_shear").dcr)}` },
      ],
    },
    {
      kind: "paragraph",
      text: "The engine produces the simple-span moment and support shear used in those comparisons. It does not publish a usable reaction packet: the row's axial basis is zero, and the out-of-plane lateral pressure has no component for the axial load-link channel. The record also excludes axial design, fire, seismic detailing, development and splices, concentrated loads, and construction-stage stability.",
    },

    { kind: "heading", level: 2, text: "In-plane shear wall", id: "shear-wall" },
    {
      kind: "paragraph",
      text: `The Shear Wall sheet binds a masonry material, unit profile, and verified capacity row. Wall length and height must match the row. ${shear.mark} is the 16 × 10 ft reinforced-CMU fixture with factored Pu 18.6 kip, Mu 500 kip-ft, and Vu 50 kip. The engine solves on the source-verified P-M branch, then reports separate masonry shear and base shear-friction checks.`,
    },
    {
      kind: "values",
      caption: `${shear.mark} · strength-level engine record.`,
      rows: [
        { label: "Axial-flexural interaction", value: `${n3(check(shear, "in_plane_axial_flexure").demand)} / ${n3(check(shear, "in_plane_axial_flexure").capacity)} kip-ft · ${pct(check(shear, "in_plane_axial_flexure").dcr)}` },
        { label: "Masonry in-plane shear", value: `${n3(check(shear, "masonry_in_plane_shear").demand)} / ${n3(check(shear, "masonry_in_plane_shear").capacity)} kip · ${pct(check(shear, "masonry_in_plane_shear").dcr)}` },
        { label: "Base shear friction", value: `${n3(check(shear, "base_shear_friction").demand)} / ${n3(check(shear, "base_shear_friction").capacity)} kip · ${pct(check(shear, "base_shear_friction").dcr)}` },
      ],
    },
    {
      kind: "shot",
      shotId: "masonry-shear-results",
      caption: `${shear.mark}: P-M governs at ${pct(check(shear, "in_plane_axial_flexure").dcr)}; shear and base friction remain separate checks.`,
    },
    {
      kind: "callout",
      variant: "note",
      title: "Ratio and current label are separate facts",
      body: `The engine returns passes=true at ${pct(check(shear, "in_plane_axial_flexure").dcr)}. The current header rounds that ratio and displays “Near limit · 92%.” This article records both shipped values without changing the verdict rule.`,
    },
    {
      kind: "paragraph",
      text: "Detailed mode prints neutral-axis and design-capacity intermediates, the exact source list, and the branch limit. It also names companion work outside the sheet: seismic system eligibility and detailing, drift, second-order effects, development, collectors, connections, foundations, and boundary elements. Those lines remain part of the exported record.",
    },

    { kind: "heading", level: 2, text: "Lintel loads and reaction types", id: "lintel" },
    {
      kind: "paragraph",
      text: "The lintel sheet accepts an effective span plus three load shapes: uniform over the span, a symmetric triangular load entered by peak intensity, and one midpoint force. Each load has a D, L, Lr, or S selector. Those type choices do not change the published ASD capacity row; they determine the per-type currency in the downstream reaction packet.",
    },
    { kind: "registry-field", calculatorId: "masonry_lintel_tms", fieldKey: "uniform_load_type" },
    {
      kind: "paragraph",
      text: `${lintel.mark} uses the exact 12 × 24 lintel row at 5.7 ft. Uniform D is 1,264 plf, triangular S peak is 200 plf, and midpoint L is 500 lb. The closed-form service statics produce 76,649.04 lb-in moment and 4,137.4 lb support shear. Against the selected row, flexure is ${pct(check(lintel, "lintel_flexure").dcr)} and shear is ${pct(check(lintel, "lintel_shear").dcr)}.`,
    },
    {
      kind: "shot",
      shotId: "masonry-lintel-results",
      caption: `${lintel.mark}: exact-row flexure and shear, followed by typed support reactions.`,
    },
    {
      kind: "values",
      caption: "Each end of ML-1 publishes the same service packet.",
      rows: [
        { label: "D · uniform", value: `${n3(lintelReaction.D ?? null)} lb` },
        { label: "S · symmetric triangle", value: `${n3(lintelReaction.S ?? null)} lb` },
        { label: "L · midpoint", value: `${n3(lintelReaction.L ?? null)} lb` },
      ],
    },
    {
      kind: "paragraph",
      text: "The limited load model matters. Off-center point loads, continuity, axial force, deflection, development, and bearing are outside this sheet; arching can enter only through the explicit load model. Read Scope & limitations before linking the reactions, because the packet proves the typed end statics, not the omitted companion checks.",
    },

    { kind: "related", topics: ["concrete-foundation-sheets", "steel-cfs-sheets", "linking-reactions", "section-properties"] },
  ],
};
